import React, { useState } from 'react';
import { MailPlusIcon, RefreshCwIcon, Trash2Icon, XIcon } from 'lucide-react';
import { useAuth, useWorkspace } from '../../contexts/AuthContext';
import { cancelInvitation, inviteMember, removeMember, resendInvitation, setMemberActive, updateMemberRole } from '../../services/teamService';
import { formatDate, fullName } from '../../utils/format';
import { Role, ROLE_LABELS } from '../../types';
import { PageHeader } from '../../components/crm/PageHeader';
import { ConfirmDialog } from '../../components/crm/ConfirmDialog';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Modal } from '../../components/ui/Modal';
import { Avatar } from '../../components/ui/Avatar';
import { Switch } from '../../components/ui/Switch';
import { Tag } from '../../components/ui/Tag';
const ROLE_OPTIONS = (Object.keys(ROLE_LABELS) as Role[]).map(role => ({
  value: role,
  label: ROLE_LABELS[role]
}));
const PERMISSIONS: {
  role: Role;
  text: string;
}[] = [{
  role: 'propietario',
  text: 'Acceso completo al espacio de trabajo.'
}, {
  role: 'administrador',
  text: 'Gestiona leads, formularios, landings, usuarios y configuración.'
}, {
  role: 'comercial',
  text: 'Gestiona leads y notas.'
}, {
  role: 'lectura',
  text: 'Puede consultar la información, pero no editarla.'
}];
export function Team() {
  const {
    organizationId,
    invitations
  } = useWorkspace();
  const {
    members
  } = useAuth();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('comercial');
  const [sending, setSending] = useState(false);
  const [toRemove, setToRemove] = useState<string | null>(null);
  const pending = invitations.filter(i => i.status === 'pendiente');
  async function handleInvite() {
    if (!email.includes('@')) return;
    setSending(true);
    await inviteMember(organizationId, email.trim(), role);
    setSending(false);
    setInviteOpen(false);
    setEmail('');
  }
  return <>
      <PageHeader title="Equipo" description="Invita a tu equipo y define qué puede hacer cada persona." actions={<Button leftIcon={<MailPlusIcon className="h-4 w-4" data-id="element-1923" data-mp="xg969dc1xr5f" />} onClick={() => setInviteOpen(true)} data-id="element-1922" data-mp="xgsrp40wuxo7">
            Invitar usuario
          </Button>} data-id="element-1921" data-mp="th0daaou5xax" />

      <section className="rounded-lg border border-border bg-background shadow-sm" data-id="element-1924" data-mp="17fro9o8ej2uu">
        <h2 className="border-b border-border px-5 py-4 text-sm font-semibold text-foreground" data-id="element-1925" data-mp="17dxe94yo4v5j">
          Miembros ({members.length})
        </h2>
        <ul className="divide-y divide-border" data-id="element-1926" data-mp="1h6mc2x1aqaie">
          {members.map(member => <li key={member.id} className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center" data-id="element-1927" data-mp="1l6wuj969yte5">
              <div className="flex min-w-0 flex-1 items-center gap-3" data-id="element-1928" data-mp="1q6hrxf5sqtor">
                <Avatar name={fullName(member.firstName, member.lastName)} data-id="element-1929" data-mp="1q452yi1mfj27" />
                <div className="min-w-0" data-id="element-1930" data-mp="1v5ud7vh0rsp8">
                  <p className="truncate text-sm font-medium text-foreground" data-id="element-1931" data-mp="2599hmj9kmlas">
                    {fullName(member.firstName, member.lastName)}
                  </p>
                  <p className="truncate text-sm text-muted-foreground" data-id="element-1932" data-mp="208v8exw8h5b6">
                    {member.email}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3" data-id="element-1933" data-mp="1rajjd0b0mfku">
                <Select id={`role-${member.id}`} options={ROLE_OPTIONS} value={member.role} disabled={member.role === 'propietario'} onChange={e => updateMemberRole(member.id, e.target.value as Role)} data-id="element-1934" data-mp="2nzhe4iirmdi0" />
                <Switch id={`active-${member.id}`} label="Acceso" checked={member.active} disabled={member.role === 'propietario'} onChange={checked => setMemberActive(member.id, checked)} data-id="element-1935" data-mp="2e9pf9c3muy7q" />
                {member.role !== 'propietario' && <Button variant="ghost" size="sm" aria-label="Eliminar miembro" onClick={() => setToRemove(member.id)} data-id="element-1936" data-mp="36ra7ie1qy0q9">
                    <Trash2Icon className="h-4 w-4" data-id="element-1937" data-mp="31xyvzzlzovs0" />
                  </Button>}
              </div>
            </li>)}
        </ul>
      </section>

      <section className="mt-5 rounded-lg border border-border bg-background shadow-sm" data-id="element-1938" data-mp="2rgbxbyok6tzf">
        <h2 className="border-b border-border px-5 py-4 text-sm font-semibold text-foreground" data-id="element-1939" data-mp="2dgu3v60bisjq">
          Invitaciones pendientes ({pending.length})
        </h2>
        {pending.length === 0 ? <p className="p-5 text-sm text-muted-foreground" data-id="element-1940" data-mp="2hkie5wgc428o">
            No hay invitaciones pendientes.
          </p> : <ul className="divide-y divide-border" data-id="element-1941" data-mp="3a73ehmesfood">
            {pending.map(invitation => <li key={invitation.id} className="flex flex-col gap-3 p-5 sm:flex-row sm:items-center sm:justify-between" data-id="element-1942" data-mp="3a6bl8i8ny319">
                <div data-id="element-1943" data-mp="30ha3onmgzdl6">
                  <p className="text-sm font-medium text-foreground" data-id="element-1944" data-mp="30grvcsmj98ww">
                    {invitation.email}
                  </p>
                  <p className="text-xs text-muted-foreground" data-id="element-1945" data-mp="3o62te2ci0p7c">
                    {ROLE_LABELS[invitation.role]} · enviada el{' '}
                    {formatDate(invitation.createdAt)}
                  </p>
                </div>
                <div className="flex gap-2" data-id="element-1946" data-mp="1hg96eny0r2bt">
                  <Button variant="secondary" size="sm" leftIcon={<RefreshCwIcon className="h-4 w-4" data-id="element-1948" data-mp="1chg2nn8japou" />} onClick={() => resendInvitation(invitation.id)} data-id="element-1947" data-mp="1ckakr842ryf3">
                    Reenviar
                  </Button>
                  <Button variant="ghost" size="sm" leftIcon={<XIcon className="h-4 w-4" data-id="element-1950" data-mp="1mbohx98k0m52" />} onClick={() => cancelInvitation(invitation.id)} data-id="element-1949" data-mp="1madhnjebhwip">
                    Cancelar
                  </Button>
                </div>
              </li>)}
          </ul>}
      </section>

      <section className="mt-5 rounded-lg border border-border bg-background p-5 shadow-sm" data-id="element-1951" data-mp="18hntp9f8ahiy">
        <h2 className="mb-4 text-sm font-semibold text-foreground" data-id="element-1952" data-mp="13j43gwtlqjks">Permisos por rol</h2>
        <ul className="grid gap-3 sm:grid-cols-2" data-id="element-1953" data-mp="2jx2n6388ar9b">
          {PERMISSIONS.map(permission => <li key={permission.role} className="rounded-lg border border-border bg-surface p-4" data-id="element-1954" data-mp="2jz6zn9k70yl7">
              <Tag data-id="element-1955" data-mp="2f03t1kokekb4">{ROLE_LABELS[permission.role]}</Tag>
              <p className="mt-2 text-sm text-muted-foreground" data-id="element-1956" data-mp="2m061evfkvakw">{permission.text}</p>
            </li>)}
        </ul>
      </section>

      <Modal open={inviteOpen} onClose={() => setInviteOpen(false)} title="Invitar a un usuario" footer={<div className="flex justify-end gap-2" data-id="element-1958" data-mp="2vzx0laf32drb">
            <Button variant="ghost" onClick={() => setInviteOpen(false)} data-id="element-1959" data-mp="30z04grjbudxp">
              Cancelar
            </Button>
            <Button onClick={handleInvite} loading={sending} data-id="element-1960" data-mp="23brbyckws4m7">
              Enviar invitación
            </Button>
          </div>} data-id="element-1957" data-mp="2qyfxd894mf7k">
        <div className="space-y-4" data-id="element-1961" data-mp="28a2m6of48h4y">
          <Input id="invite-email" label="Email" type="email" placeholder="nombre@empresa.com" value={email} onChange={e => setEmail(e.target.value)} data-id="element-1962" data-mp="2d95s0b2x6rdz" />
          <Select id="invite-role" label="Rol" options={ROLE_OPTIONS.filter(r => r.value !== 'propietario')} value={role} onChange={e => setRole(e.target.value as Role)} data-id="element-1963" data-mp="2i15mho080s36" />
        </div>
      </Modal>

      <ConfirmDialog open={Boolean(toRemove)} title="Eliminar miembro" description="Perderá el acceso a este espacio de trabajo inmediatamente." onCancel={() => setToRemove(null)} onConfirm={async () => {
      if (toRemove) await removeMember(toRemove);
      setToRemove(null);
    }} data-id="element-1964" data-mp="1phqk82sdjhvc" />
    </>;
}